import { VerticalSlug } from './types'
import { Layers } from 'lucide-react'

export interface VerticalProductFieldsProps {
  slug: VerticalSlug
  values: Record<string, any>
  onChange: (field: string, value: any) => void
  errors?: Record<string, string>
}

export function VerticalProductFields({
  slug,
  values,
  onChange,
  errors: _errors = {},
}: VerticalProductFieldsProps) {
  const getVal = (key: string, defaultVal: any = '') => values?.[key] ?? defaultVal

  if (slug === 'otro' || slug === 'tienda') {
    return null
  }

  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/30 p-4 space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-emerald-100/60">
        <Layers className="w-4 h-4 text-[#075E54]" />
        <h4 className="text-xs font-bold uppercase tracking-wider text-[#075E54]">
          Atributos Específicos del Sector
        </h4>
      </div>

      {slug === 'inmobiliaria' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Tipo de Inmueble</label>
            <select
              value={getVal('propertyType')}
              onChange={(e) => onChange('propertyType', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="">Seleccionar...</option>
              <option value="apartamento">Apartamento</option>
              <option value="casa">Casa</option>
              <option value="oficina">Oficina</option>
              <option value="terreno">Terreno</option>
              <option value="local">Local comercial</option>
              <option value="galpon">Galpón</option>
              <option value="ph">Penthouse (PH)</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Operación</label>
            <select
              value={getVal('operation', 'alquiler')}
              onChange={(e) => onChange('operation', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="alquiler">Alquiler</option>
              <option value="venta">Venta</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Metraje (m²)</label>
            <input
              type="number"
              step="0.1"
              value={getVal('areaM2')}
              onChange={(e) => onChange('areaM2', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 120"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Habitaciones</label>
            <input
              type="number"
              value={getVal('rooms')}
              onChange={(e) => onChange('rooms', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 3"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Baños</label>
            <input
              type="number"
              value={getVal('bathrooms')}
              onChange={(e) => onChange('bathrooms', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 2"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Puestos de Estacionamiento</label>
            <input
              type="number"
              value={getVal('parkingSpots')}
              onChange={(e) => onChange('parkingSpots', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 1"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block font-bold text-slate-600 mb-1">Zona / Ubicación</label>
            <input
              type="text"
              value={getVal('zone')}
              onChange={(e) => onChange('zone', e.target.value)}
              placeholder="Ej: Las Mercedes, Caracas"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            <label className="block font-bold text-slate-600 mb-1">Servicios y Comodidades Clave</label>
            <input
              type="text"
              value={getVal('utilities')}
              onChange={(e) => onChange('utilities', e.target.value)}
              placeholder="Ej: Pozo de agua profundo, planta eléctrica 100%, vigilancia privada"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>
        </div>
      )}

      {slug === 'salud' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Especialidad Médica</label>
            <input
              type="text"
              value={getVal('specialty')}
              onChange={(e) => onChange('specialty', e.target.value)}
              placeholder="Ej: Cardiología, Pediatría, Odontología"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Profesional / Especialista</label>
            <input
              type="text"
              value={getVal('practitioner')}
              onChange={(e) => onChange('practitioner', e.target.value)}
              placeholder="Ej: Dr. Alejandro Ramos"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Duración de Consulta (Minutos)</label>
            <input
              type="number"
              value={getVal('durationMin')}
              onChange={(e) => onChange('durationMin', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 45"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Modalidad</label>
            <select
              value={getVal('modality', 'presencial')}
              onChange={(e) => onChange('modality', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="presencial">Presencial en Consultorio</option>
              <option value="telemedicina">Telemedicina / Online</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="block font-bold text-slate-600 mb-1">Preparación previa / Indicaciones</label>
            <input
              type="text"
              value={getVal('prepInstructions')}
              onChange={(e) => onChange('prepInstructions', e.target.value)}
              placeholder="Ej: 8 horas de ayuno para análisis de laboratorio, traer orden médica"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>
        </div>
      )}

      {slug === 'belleza_estetica' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Duración Estimada (Minutos)</label>
            <input
              type="number"
              value={getVal('durationMin')}
              onChange={(e) => onChange('durationMin', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 60"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Abono de Reserva Requerido</label>
            <input
              type="text"
              value={getVal('depositRequired')}
              onChange={(e) => onChange('depositRequired', e.target.value)}
              placeholder="Ej: 50% o $10"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Estilista / Especialista</label>
            <input
              type="text"
              value={getVal('specialist')}
              onChange={(e) => onChange('specialist', e.target.value)}
              placeholder="Ej: Laura estilista"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>
        </div>
      )}

      {slug === 'turismo_posadas' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Capacidad Máx. (Pax)</label>
            <input
              type="number"
              value={getVal('capacityPax')}
              onChange={(e) => onChange('capacityPax', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 4"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Tipo de Plan</label>
            <select
              value={getVal('planType', 'solo_hospedaje')}
              onChange={(e) => onChange('planType', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="solo_hospedaje">Solo Hospedaje</option>
              <option value="todo_incluido">Todo Incluido</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Noches Mínimas</label>
            <input
              type="number"
              value={getVal('minNights', 1)}
              onChange={(e) => onChange('minNights', e.target.value ? Number(e.target.value) : undefined)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              id="boatTourIncluded"
              checked={Boolean(getVal('boatTourIncluded', false))}
              onChange={(e) => onChange('boatTourIncluded', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#075E54] focus:ring-[#075E54]"
            />
            <label htmlFor="boatTourIncluded" className="font-bold text-slate-700 cursor-pointer">
              Incluye paseo en lancha
            </label>
          </div>
        </div>
      )}

      {slug === 'restaurante' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Tiempo de Preparación (Minutos)</label>
            <input
              type="number"
              value={getVal('prepTimeMin')}
              onChange={(e) => onChange('prepTimeMin', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 20"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Tipo de Ítem</label>
            <select
              value={getVal('itemType', 'plato')}
              onChange={(e) => onChange('itemType', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="plato">Plato Principal</option>
              <option value="combo">Combo con Ahorro</option>
              <option value="bebida">Bebida</option>
              <option value="extra">Acompañante / Extra</option>
            </select>
          </div>

          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              id="availableToday"
              checked={getVal('availableToday', true)}
              onChange={(e) => onChange('availableToday', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#075E54] focus:ring-[#075E54]"
            />
            <label htmlFor="availableToday" className="font-bold text-slate-700 cursor-pointer">
              Disponible en la carta de hoy
            </label>
          </div>
        </div>
      )}

      {slug === 'tienda_repuestos' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Marca del Vehículo</label>
            <input
              type="text"
              value={getVal('brand')}
              onChange={(e) => onChange('brand', e.target.value)}
              placeholder="Ej: Toyota, Ford, Chevrolet"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Modelo</label>
            <input
              type="text"
              value={getVal('model')}
              onChange={(e) => onChange('model', e.target.value)}
              placeholder="Ej: Corolla, Fiesta, Spark"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Años Compatibles</label>
            <input
              type="text"
              value={getVal('yearRange')}
              onChange={(e) => onChange('yearRange', e.target.value)}
              placeholder="Ej: 2012-2018"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Tipo de Repuesto</label>
            <select
              value={getVal('partType', 'oem')}
              onChange={(e) => onChange('partType', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="oem">Original (OEM)</option>
              <option value="reemplazo_a">Reemplazo Clase A</option>
              <option value="generico">Genérico / Alternativo</option>
            </select>
          </div>
        </div>
      )}

      {slug === 'bodegon_licoreria' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Temperatura de Entrega</label>
            <select
              value={getVal('temperature', 'frio')}
              onChange={(e) => onChange('temperature', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="frio">Bien Frío (Listo para tomar)</option>
              <option value="natural">Temperatura Ambiente</option>
              <option value="indiferente">Indiferente</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Presentación / Envase</label>
            <input
              type="text"
              value={getVal('presentation')}
              onChange={(e) => onChange('presentation', e.target.value)}
              placeholder="Ej: Botella 750ml, Six-Pack"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              id="bundleComboReady"
              checked={Boolean(getVal('bundleComboReady', false))}
              onChange={(e) => onChange('bundleComboReady', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#075E54] focus:ring-[#075E54]"
            />
            <label htmlFor="bundleComboReady" className="font-bold text-slate-700 cursor-pointer">
              Apto para combo hielo / snacks
            </label>
          </div>
        </div>
      )}

      {slug === 'ferreteria' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Unidad de Medida</label>
            <input
              type="text"
              value={getVal('unitOfMeasure')}
              onChange={(e) => onChange('unitOfMeasure', e.target.value)}
              placeholder="Ej: Saco, Metro, Unidad, Galón"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Calibre / Medida</label>
            <input
              type="text"
              value={getVal('gaugeOrSize')}
              onChange={(e) => onChange('gaugeOrSize', e.target.value)}
              placeholder="Ej: 1/2 pulgada, 2x4, Calibre 18"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Tipo de Despacho</label>
            <select
              value={getVal('dispatchType', 'retiro_tienda')}
              onChange={(e) => onChange('dispatchType', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="retiro_tienda">Solo Retiro en Tienda</option>
              <option value="moto">Despacho en Moto / Liviano</option>
              <option value="flete_pesado">Flete Pesado / Camión</option>
            </select>
          </div>
        </div>
      )}

      {slug === 'servicios' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Modalidad del Servicio</label>
            <select
              value={getVal('modality', 'presencial')}
              onChange={(e) => onChange('modality', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="presencial">Presencial (en sede o a domicilio)</option>
              <option value="remoto">Remoto / Online</option>
              <option value="hibrido">Híbrido</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Duración Estimada</label>
            <input
              type="text"
              value={getVal('estimatedDuration')}
              onChange={(e) => onChange('estimatedDuration', e.target.value)}
              placeholder="Ej: 2 horas, 1 día hábil"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>
        </div>
      )}

      {slug === 'tecnologia' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Garantía (Meses)</label>
            <input
              type="number"
              value={getVal('warrantyMonths')}
              onChange={(e) => onChange('warrantyMonths', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 12"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Condición del Equipo</label>
            <select
              value={getVal('condition', 'nuevo')}
              onChange={(e) => onChange('condition', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="nuevo">Nuevo en Caja</option>
              <option value="reacondicionado">Reacondicionado Grado A</option>
              <option value="usado">Usado en Buen Estado</option>
            </select>
          </div>
        </div>
      )}

      {slug === 'educacion' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs">
          <div>
            <label className="block font-bold text-slate-600 mb-1">Modalidad</label>
            <select
              value={getVal('modality', 'online')}
              onChange={(e) => onChange('modality', e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            >
              <option value="online">Online en vivo / Grabado</option>
              <option value="presencial">Presencial en Campus</option>
              <option value="hibrido">Híbrido</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Duración Total</label>
            <input
              type="text"
              value={getVal('duration')}
              onChange={(e) => onChange('duration', e.target.value)}
              placeholder="Ej: 4 semanas (32 horas)"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-600 mb-1">Cupos Disponibles</label>
            <input
              type="number"
              value={getVal('slots')}
              onChange={(e) => onChange('slots', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Ej: 25"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none"
            />
          </div>
        </div>
      )}
    </div>
  )
}
